import React from "react";
import { owner, team, ownerPhotoId, ownerObjectPosition, driveThumb } from "../data/categoriesData";

// Founder spotlight + crew grid. Each member shows their photo as a tile
// background with name/role overlaid at the bottom in editorial type.
export const Team = () => {
  return (
    <section
      id="team"
      data-testid="team-section"
      className="relative bg-black border-t border-white/5 py-16 sm:py-24 lg:py-28"
    >
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="mb-12 sm:mb-16 lg:mb-20">
          <h2 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
            Made
            <br />
            by <em className="italic font-medium">hands</em>.
          </h2>
        </div>

        {/* Founder spotlight: photo + bio side-by-side */}
        <div
          data-testid="team-founder"
          className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 sm:mb-20 border border-white/10 overflow-hidden"
        >
          <div className="md:col-span-5 relative aspect-[4/5] md:aspect-auto md:min-h-[460px] bg-neutral-900 overflow-hidden">
            <img
              src={driveThumb(ownerPhotoId, 1200)}
              alt={owner.name}
              data-testid="team-founder-photo"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: ownerObjectPosition }}
              loading="lazy"
            />
          </div>
          <div className="md:col-span-7 p-6 sm:p-10 lg:p-14 flex flex-col justify-center">
            <div className="eyebrow mb-3">Founder &amp; Owner</div>
            <h3 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95]">
              {owner.name}
              <span className="text-white/60">.</span>
            </h3>
            <div className="eyebrow mt-5 text-white/50">{owner.role}</div>
            <p className="mt-6 sm:mt-8 text-white/75 text-base sm:text-lg font-light leading-relaxed max-w-xl">
              {owner.bio}
            </p>
          </div>
        </div>

        {/* Team grid: smaller portrait tiles, denser layout */}
        <div className="eyebrow mb-6 text-white/40">The Team</div>
        <div
          data-testid="team-grid"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4"
        >
          {team.map((m, i) => (
            <div
              key={m.name}
              data-testid={`team-member-${i}`}
              className="group relative aspect-[3/4] overflow-hidden bg-neutral-900 border border-white/10"
            >
              {m.photoId ? (
                <img
                  src={driveThumb(m.photoId, 700)}
                  alt={m.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  style={{
                    objectPosition: m.objectPosition || "50% 25%",
                    transform: m.scale ? `scale(${m.scale})` : undefined,
                    transformOrigin: m.objectPosition || "50% 25%",
                  }}
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-transparent" />
              <div className="absolute bottom-2.5 left-2.5 right-2.5 sm:bottom-3.5 sm:left-3.5 sm:right-3.5">
                <div className="font-serif italic text-white text-base sm:text-lg lg:text-xl leading-tight mb-1">
                  {m.name}
                </div>
                <div className="eyebrow text-white/70 text-[9px] sm:text-[10px]">{m.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;
